/**
 * @file SettingsGroup.tsx
 * @description Группа настроек панели — карточка со сворачиванием
 * (phase19.16, SB122; uiux-settings-groups §1, §8 — эталон «Настройки
 * страницы»: белая карточка на подложке, скругление 12, тонкая линия, как
 * `SectionCard`).
 *
 * Заголовок `type-section-title` (18/600) до двух строк без обрезки; вся
 * строка — кнопка сворачивания с шевроном справа, высота `--control-h`
 * (48 до 1024, 44 с 1024). У свёрнутой справа сводка (уже 340 px — под
 * заголовком), у открытой — строка назначения под заголовком. Иконок у
 * групп нет — только значок состояния: ✕ ошибка, ! предупреждение, замок.
 * Содержимое свёрнутой остаётся в DOM (`hidden="until-found"`: поиск по
 * странице раскрывает группу). До 768 заголовок открытой группы липкий.
 *
 * ```tsx
 * <SettingsGroup id="fill" title="Заливка" summary="Плашка" hint="Цвет фона подвала." status="warning">…</SettingsGroup>
 * ```
 */

import { Collapsible } from "@base-ui/react/collapsible";
import { ChevronDown, CircleAlert, CircleX, Lock, type LucideIcon } from "lucide-react";
import { useContext, useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { SettingsGroupsContext, type SettingsGroupStatus } from "./context";
import { cn } from "@/lib/utils";

/** Значки состояния — реестром: новый вид = запись. */
const STATUS_META: Readonly<Record<SettingsGroupStatus, { icon: LucideIcon; className: string; label: string }>> = {
  error: { icon: CircleX, className: "text-destructive", label: "нужно исправить" },
  warning: { icon: CircleAlert, className: "text-warning", label: "есть предупреждение" },
  readonly: { icon: Lock, className: "text-on-surface-variant", label: "только просмотр" },
};

export interface SettingsGroupProps {
  /** Ключ группы: якорь `#group-<id>`, `?group=<id>`, `openGroup(id)`. */
  id: string;
  title: string;
  /** Подпись в оглавлении, до 12 знаков; нет — заголовок. */
  tocLabel?: string;
  /** Сводка: видна у свёрнутой группы. */
  summary?: ReactNode;
  /** Назначение одной строкой под заголовком открытой группы. */
  hint?: string;
  status?: SettingsGroupStatus;
  /** Курсор над группой (подсветка на канве). */
  onHover?: (inside: boolean) => void;
  /** Уровень заголовка: h3 — под заголовком панели (h2). */
  as?: "h2" | "h3";
  children: ReactNode;
  className?: string;
}

export function SettingsGroup({ id, title, tocLabel, summary, hint, status, onHover, as: Heading = "h3", children, className }: SettingsGroupProps) {
  const ctx = useContext(SettingsGroupsContext);
  const ref = useRef<HTMLElement | null>(null);
  // Вне `SettingsGroups` — своё состояние, открыта.
  const [localOpen, setLocalOpen] = useState(true);
  const open = ctx ? ctx.isOpen(id) : localOpen;
  const register = ctx?.register;
  const titleId = `group-${id}-title`;

  // До отрисовки: иначе на телефоне первый кадр — все свёрнуты (открытая выбирается из зарегистрированных).
  useLayoutEffect(() => register?.({ id, title, tocLabel, status, el: ref.current }), [register, id, title, tocLabel, status]);

  const meta = status ? STATUS_META[status] : null;
  const flash = ctx?.flashId === id;

  return (
    <section
      ref={ref}
      id={`group-${id}`}
      aria-labelledby={titleId}
      data-slot="settings-group"
      data-status={status}
      data-flash={flash ? "" : undefined}
      onMouseEnter={onHover ? () => onHover(true) : undefined}
      onMouseLeave={onHover ? () => onHover(false) : undefined}
      className={cn(
        "flex min-w-0 scroll-mt-[calc(var(--panel-sticky-h,0px)+0.5rem)] flex-col rounded-xl bg-surface-lowest p-4",
        "[box-shadow:inset_0_0_0_1px_var(--border)] transition-[box-shadow] duration-500",
        flash && "[box-shadow:inset_0_0_0_2px_var(--primary)]",
        className,
      )}
    >
      <Collapsible.Root open={open} onOpenChange={(next) => (ctx ? ctx.setOpen(id, next) : setLocalOpen(next))}>
        <Heading
          className={cn(
            "m-0",
            // До 768 заголовок открытой группы липкий — под общей липкой строкой панели.
            open && "max-md:sticky max-md:top-[var(--panel-sticky-h,0px)] max-md:z-[1] max-md:bg-surface-lowest",
          )}
        >
          <Collapsible.Trigger
            data-settings-group-trigger=""
            className={cn(
              "group/sg -mx-2 flex min-h-[var(--control-h,3rem)] w-[calc(100%+1rem)] items-center gap-2 rounded-lg px-2 text-left",
              "outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <span className="@container min-w-0 flex-1">
              <span className="flex min-w-0 flex-col py-1 @min-[340px]:flex-row @min-[340px]:items-center @min-[340px]:gap-3">
                <span id={titleId} className="type-section-title min-w-0 flex-1 break-words text-on-surface">
                  {title}
                  {meta ? <StatusIcon meta={meta} /> : null}
                </span>
                {summary && !open ? (
                  <span
                    data-slot="settings-group-summary"
                    className={cn(
                      "type-caption min-w-0 break-words @min-[340px]:max-w-[50%] @min-[340px]:text-right",
                      status === "error" ? "text-destructive" : "text-on-surface-variant",
                    )}
                  >
                    {summary}
                  </span>
                ) : null}
              </span>
            </span>
            <ChevronDown aria-hidden className="size-5 shrink-0 text-on-surface-variant transition-transform group-data-[panel-open]/sg:rotate-180" />
          </Collapsible.Trigger>
        </Heading>
        <Collapsible.Panel hiddenUntilFound className="min-w-0">
          <div className="flex min-w-0 flex-col gap-3 pt-3">
            {hint ? <p className="type-caption -mt-2 break-words text-on-surface-variant">{hint}</p> : null}
            {children}
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </section>
  );
}

function StatusIcon({ meta }: { meta: (typeof STATUS_META)[SettingsGroupStatus] }) {
  const Icon = meta.icon;
  return (
    <span className="ml-2 inline-flex translate-y-0.5 align-baseline">
      <Icon aria-hidden className={cn("size-4", meta.className)} />
      <span className="sr-only">— {meta.label}</span>
    </span>
  );
}
